import { COLLISION_CELLS_PER_TILE, type DoorLayer, type EntityInstance, type LayerDocument, type MapDocument, type PropInstance, type TileRef } from "../document/types.ts";
import { issueIgnoreKey, type IssueTarget } from "../document/issue-ignores.ts";
import type { IssueRemedy, ValidationIssue } from "./adapter.ts";
import { ignoredBy, issueIgnorable, type IssueIgnored } from "./issue-ignores.ts";
import { propFootprint } from "./prop-hit.ts";

/**
 * The Issues list's lines (P1.6-T29, brief 6.3 item 7): each issue a check reports, Daedalus's own or the
 * game's `validate`, as what is wrong, where (the thing's name, its layer and its position in world pixels)
 * and the fix, naming things by name where the check wrote an id, with the thing Go to selects and the box it
 * frames. A check writes its message as what is wrong, then `; ` and the fix (the compile's refusal reads
 * the same words); a message with no `; ` has no fix of its own. The words are rewritten, never the checks:
 * a lead `prop <id> ` reads "This prop ", a lead `arrival point <id> ` "This arrival point ", a lead
 * `door <name> ` "This door ", a lead `map <id> ` "This map ", a frame id on one of the document's tilesets the
 * frame's name, and a marker type's id its label wherever the id is not simply its label's word.
 */

export type { IssueTarget };

/**
 * A box in world pixels.
 */
export interface IssueBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * One line of the Issues list. `severity` is `error` for what the compile refuses, `warning` for what compiles
 * and `info` for what may well be meant (a one-way door, ADR-122 decision 2.4); `position` is the thing's anchor in whole world pixels (a prop's anchor cell's top-left, as
 * Placement types it; a marker's point), null for a layer or the map; `layer` is null when the thing is a
 * layer or the map; `remedy` is the fix the editor can make itself (`EditorStore.fixIssue`) with its button's
 * word, null when the fix is the author's to make; `alternative` is what the author may do instead of the fix,
 * accepting what the line reports (Keep as placed, ADR-040 amendment of 2026-10-04), null when there is none.
 * `kind` is the id the check declared, null when it declared none; `ignorable` whether the author may ignore
 * the line (`issueIgnorable`), with `ignoreKey`, the key of an ignore of this line, and `kindKey`, the key of
 * an ignore of every line of its kind on the map, both null when it may not be; `ignored` is the record that
 * hides the line, null on a line the list shows (ADR-070's amendment of 2026-10-05). `frame` is the frame a line
 * about no placed thing is about, which its fix acts on; absent on every other line.
 */
export interface IssueEntry {
  readonly key: string;
  readonly severity: "error" | "warning" | "info";
  readonly what: string;
  readonly fix: string | null;
  readonly remedy: { readonly id: IssueRemedy; readonly label: string } | null;
  readonly alternative: { readonly id: IssueRemedy; readonly label: string } | null;
  readonly name: string;
  readonly layer: string | null;
  readonly position: { readonly x: number; readonly y: number } | null;
  readonly target: IssueTarget;
  readonly box: IssueBox;
  readonly kind: string | null;
  readonly ignorable: boolean;
  readonly ignoreKey: string | null;
  readonly kindKey: string | null;
  readonly ignored: IssueIgnored | null;
  readonly frame?: TileRef;
}

/**
 * A map's lines apart: the ones the Issues list shows and its count takes, the ones a record of the map hides,
 * each naming that record, and how many of the map's records hide no line now.
 */
export interface IssueReport {
  readonly shown: readonly IssueEntry[];
  readonly ignored: readonly IssueEntry[];
  readonly dormant: number;
}

/**
 * A marker's place: its type's label, its layer's name, its point in whole world pixels, the marker as Go to's
 * target and its tile as the box.
 */
export interface MarkerPlace {
  readonly name: string;
  readonly layer: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly target: { readonly kind: "marker"; readonly layerId: string; readonly entityId: string };
  readonly box: IssueBox;
}

/**
 * A prop's place: its name, its layer's name, its position in whole world pixels, the prop as Go to's target
 * and the box its art covers.
 */
export interface PropPlace {
  readonly name: string;
  readonly layer: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly target: { readonly kind: "prop"; readonly layerId: string; readonly propId: string };
  readonly box: IssueBox;
}

/**
 * What the lines read names and sizes from: the tile size, a frame's name and pixel size (undefined when the
 * context holds none, when the prop covers its one cell), the registered marker types and the ids of the
 * document's tilesets, the only tilesets a frame id in the words is read against.
 */
export interface IssueNames {
  readonly tileSize: number;
  readonly frameName: (frame: TileRef) => string;
  readonly frameSize: (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;
  readonly entityTypes: readonly { readonly type: string; readonly label: string }[];
  readonly tilesets: readonly string[];
}

/**
 * The word on the button of each fix the editor makes itself.
 */
const REMEDY_LABELS: Readonly<Record<IssueRemedy, string>> = { "align-prop": "Align", "align-prop-alone": "Align alone", "keep-prop-placement": "Keep as placed", "remove-pull": "Remove", "remove-layer": "Remove layer" };

const FRAME_ID = /([^\s/(),;]+)\/[^\s/(),;]+\/(\d+)/g;

/**
 * A pattern for a whole word, its characters taken literally.
 *
 * @param word The word.
 * @returns The pattern, global.
 */
function wordPattern(word: string): RegExp {
  return new RegExp(`(?<![\\w-])${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\w-])`, "g");
}

/**
 * The issue's words with ids read as names: the lead, frame ids and marker type ids.
 *
 * @param text The words.
 * @param issue The issue, for the prop's or the marker's id.
 * @param map The map, for its id.
 * @param names The lookups.
 * @param renames The marker type ids to read as their labels, each with its pattern.
 * @param doorName The name of the door the issue points at, or null when it points at none.
 * @returns The words.
 */
function named(text: string, issue: ValidationIssue, map: MapDocument, names: IssueNames, renames: readonly { readonly pattern: RegExp; readonly label: string }[], doorName: string | null): string {
  const tilesets = names.tilesets;
  const leads = [
    ...(issue.propId === undefined ? [] : [{ lead: `prop ${issue.propId} `, words: "This prop " }]),
    ...(issue.entityId === undefined ? [] : [{ lead: `marker ${issue.entityId} `, words: "This marker " }, { lead: `arrival point ${issue.entityId} `, words: "This arrival point " }]),
    ...(doorName === null ? [] : [{ lead: `door ${doorName} `, words: "This door " }]),
    { lead: `map ${map.id} `, words: "This map " },
  ];
  const led = leads.find((lead) => text.startsWith(lead.lead));
  let words = led === undefined ? text : `${led.words}${text.slice(led.lead.length)}`;
  words = words.replace(FRAME_ID, (match: string, tileset: string, tile: string) => (tilesets.includes(tileset) ? names.frameName({ tileset, tile: Number(tile) }) : match));
  for (const { pattern, label } of renames) {
    words = words.replace(pattern, label);
  }
  return words;
}

/**
 * Splits a message into what is wrong and the fix, the fix's first letter raised.
 *
 * @param message The message.
 * @returns What and the fix, null without one.
 */
function split(message: string): { readonly what: string; readonly fix: string | null } {
  const at = message.indexOf("; ");
  if (at < 0) {
    return { what: message, fix: null };
  }
  const fix = message.slice(at + 2);
  return { what: message.slice(0, at), fix: fix.charAt(0).toUpperCase() + fix.slice(1) };
}

/**
 * A map's layers by id, and each layer's props and markers by id and its doors' painted boxes in collision cells,
 * indexed once per layer on first use, so a list of many lines on a map of many props or doors stays linear.
 */
interface MapIndex {
  readonly layers: ReadonlyMap<string, LayerDocument>;
  readonly props: (layerId: string) => ReadonlyMap<string, PropInstance>;
  readonly entities: (layerId: string) => ReadonlyMap<string, EntityInstance>;
  readonly doorCells: (layer: DoorLayer) => ReadonlyMap<number, IssueBox>;
}

/**
 * The box of the cells each door of a layer paints, in collision cells, by the door's 1-based place in the list.
 *
 * @param layer The door layer.
 * @param width The map's width in collision cells.
 * @returns The boxes; a door that paints no cell has none.
 */
function paintedDoorCells(layer: DoorLayer, width: number): Map<number, IssueBox> {
  const bounds = new Map<number, { x0: number; y0: number; x1: number; y1: number }>();
  for (let i = 0; i < layer.cells.length; i += 1) {
    const value = layer.cells[i] as number;
    if (value === 0) {
      continue;
    }
    const x = i % width;
    const y = (i - x) / width;
    const held = bounds.get(value);
    if (held === undefined) {
      bounds.set(value, { x0: x, y0: y, x1: x, y1: y });
    } else {
      held.x0 = Math.min(held.x0, x);
      held.x1 = Math.max(held.x1, x);
      held.y1 = y;
    }
  }
  return new Map([...bounds].map(([value, b]) => [value, { x: b.x0, y: b.y0, w: b.x1 - b.x0 + 1, h: b.y1 - b.y0 + 1 }]));
}

/**
 * The index over a map.
 *
 * @param map The map.
 * @returns The index.
 */
function indexOf(map: MapDocument): MapIndex {
  const layers = new Map(map.layers.map((layer) => [layer.id, layer]));
  const props = new Map<string, ReadonlyMap<string, PropInstance>>();
  const entities = new Map<string, ReadonlyMap<string, EntityInstance>>();
  const doors = new Map<string, ReadonlyMap<number, IssueBox>>();
  return {
    layers,
    props: (layerId) => {
      const layer = layers.get(layerId);
      let byId = props.get(layerId);
      if (byId === undefined) {
        byId = new Map(layer?.kind === "prop" ? layer.props.map((p) => [p.id, p]) : []);
        props.set(layerId, byId);
      }
      return byId;
    },
    entities: (layerId) => {
      const layer = layers.get(layerId);
      let byId = entities.get(layerId);
      if (byId === undefined) {
        byId = new Map(layer?.kind === "entity" ? layer.entities.map((e) => [e.id, e]) : []);
        entities.set(layerId, byId);
      }
      return byId;
    },
    doorCells: (layer) => {
      let boxes = doors.get(layer.id);
      if (boxes === undefined) {
        boxes = paintedDoorCells(layer, map.width * COLLISION_CELLS_PER_TILE);
        doors.set(layer.id, boxes);
      }
      return boxes;
    },
  };
}

/**
 * The cells a placed prop's art covers, in world pixels: the box Go to frames (P1.6-T29) and the command
 * palette frames a prop it finds by name in (P1.6-T26). A frame the lookup cannot size covers its one cell.
 *
 * @param prop The prop.
 * @param frameSize A frame's pixel size, undefined when unknown.
 * @param tileSize The tile size in pixels.
 * @returns The box.
 */
export function propBox(prop: PropInstance, frameSize: (frame: TileRef) => { readonly w: number; readonly h: number } | undefined, tileSize: number): IssueBox {
  const cells = propFootprint(prop, (tileset, tile) => ({ ...(frameSize({ tileset, tile }) ?? { w: tileSize, h: tileSize }), base: 0 }), tileSize);
  return { x: cells.x0 * tileSize, y: cells.y0 * tileSize, w: (cells.x1 - cells.x0) * tileSize, h: (cells.y1 - cells.y0) * tileSize };
}

/**
 * Where a prop stands, as a line names it and Go to frames it: its frame's name, its layer's name, its anchor
 * cell's top-left in whole world pixels, the prop as the target and the cells its art covers as the box. The
 * Issues list's lines and the Behaviours view's things share it, so both name and frame a prop alike.
 *
 * @param prop The prop.
 * @param layer The prop layer holding it.
 * @param names The lookups.
 * @returns The place.
 */
export function propPlace(prop: PropInstance, layer: { readonly id: string; readonly name: string }, names: IssueNames): PropPlace {
  const size = names.tileSize;
  return {
    name: names.frameName(prop.frame),
    layer: layer.name,
    position: { x: Math.round(prop.x * size), y: Math.round(prop.y * size) },
    target: { kind: "prop", layerId: layer.id, propId: prop.id },
    box: propBox(prop, names.frameSize, size),
  };
}

/**
 * Where a marker stands, as a line names it and Go to frames it: its type's label, its layer's name, its point
 * in whole world pixels, the marker as the target and its tile as the box.
 *
 * @param entity The marker.
 * @param layer The marker layer holding it.
 * @param names The lookups.
 * @returns The place.
 */
export function markerPlace(entity: EntityInstance, layer: { readonly id: string; readonly name: string }, names: IssueNames): MarkerPlace {
  const size = names.tileSize;
  return {
    name: names.entityTypes.find((t) => t.type === entity.type)?.label ?? entity.type,
    layer: layer.name,
    position: { x: Math.round(entity.x * size), y: Math.round(entity.y * size) },
    target: { kind: "marker", layerId: layer.id, entityId: entity.id },
    box: { x: Math.floor(entity.x) * size, y: Math.floor(entity.y) * size, w: size, h: size },
  };
}

/**
 * Where an issue points, from the ids it carries: its prop, its marker, its door, its layer, else the map. An id
 * the map no longer holds falls back to its layer, then the map. A door's box is the cells it paints, the whole
 * map while it paints none, and its position the box's corner, null while it paints none. An issue that carries a
 * `box` and no thing is framed by it.
 *
 * @param issue The issue.
 * @param map The map.
 * @param names The lookups.
 * @param things The map's layers, props and markers by id.
 * @returns The place of the line.
 */
function placeOf(issue: ValidationIssue, map: MapDocument, names: IssueNames, things: MapIndex): Pick<IssueEntry, "name" | "layer" | "position" | "target" | "box"> {
  const size = names.tileSize;
  const whole: IssueBox = { x: 0, y: 0, w: map.width * size, h: map.height * size };
  const layer: LayerDocument | undefined = issue.layerId === undefined ? undefined : things.layers.get(issue.layerId);
  if (layer?.kind === "prop" && issue.propId !== undefined) {
    const prop = things.props(layer.id).get(issue.propId);
    if (prop !== undefined) {
      return propPlace(prop, layer, names);
    }
  }
  if (layer?.kind === "entity" && issue.entityId !== undefined) {
    const entity = things.entities(layer.id).get(issue.entityId);
    if (entity !== undefined) {
      return markerPlace(entity, layer, names);
    }
  }
  if (layer?.kind === "door" && issue.doorId !== undefined) {
    const at = layer.doors.findIndex((door) => door.id === issue.doorId);
    const door = layer.doors[at];
    if (door !== undefined) {
      const cells = things.doorCells(layer).get(at + 1);
      const cell = size / COLLISION_CELLS_PER_TILE;
      const box = cells === undefined ? whole : { x: cells.x * cell, y: cells.y * cell, w: cells.w * cell, h: cells.h * cell };
      return { name: door.name, layer: layer.name, position: cells === undefined ? null : { x: box.x, y: box.y }, target: { kind: "door", layerId: layer.id, doorId: door.id }, box };
    }
  }
  const box = issue.box ?? whole;
  if (layer !== undefined) {
    return { name: layer.name, layer: null, position: null, target: { kind: "layer", layerId: layer.id }, box };
  }
  return { name: map.name, layer: null, position: null, target: { kind: "map" }, box };
}

/**
 * The Issues list's lines for one map: its issues that no record of the map hides, refusals, then warnings, then infos
 * and each group in the order the checks gave them, every key distinct.
 *
 * @param map The map.
 * @param issues The issues, as `EditorStore.validate` returns them; another map's are left out.
 * @param names The lookups.
 * @returns The lines.
 */
export function issueEntries(map: MapDocument, issues: readonly ValidationIssue[], names: IssueNames): readonly IssueEntry[] {
  return issueReport(map, issues, names).shown;
}

/**
 * One map's lines, shown and ignored (ADR-070's amendment of 2026-10-05): a line is ignored when it may be
 * (`issueIgnorable`) and the map holds a record for its kind on its target or on the map. No record hides a
 * line that stops the compile.
 *
 * @param map The map.
 * @param issues The issues, as `EditorStore.validate` returns them; another map's are left out.
 * @param names The lookups.
 * @returns The lines apart, each group refusals, then warnings, then infos, in the checks' order, and the count of the
 *   map's records that hide none.
 */
export function issueReport(map: MapDocument, issues: readonly ValidationIssue[], names: IssueNames): IssueReport {
  const seen = new Map<string, number>();
  const matched = new Set<string>();
  const entries: IssueEntry[] = [];
  const things = indexOf(map);
  const renames = names.entityTypes.filter(({ type, label }) => type !== label.toLowerCase()).map(({ type, label }) => ({ pattern: wordPattern(type), label }));
  for (const issue of issues) {
    if (issue.mapId !== map.id) {
      continue;
    }
    const place = placeOf(issue, map, names, things);
    const words = split(named(issue.message, issue, map, names, renames, place.target.kind === "door" ? place.name : null));
    const base = `${JSON.stringify(place.target)} ${issue.message}`;
    const repeat = seen.get(base) ?? 0;
    seen.set(base, repeat + 1);
    const ignorable = issueIgnorable(issue);
    const kind = issue.kind ?? null;
    const ignored = ignorable ? ignoredBy(map.ignoredIssues, kind as string, place.target) : null;
    if (ignored !== null) {
      matched.add(ignored.key);
    }
    entries.push({
      key: repeat === 0 ? base : `${base} #${repeat + 1}`,
      severity: issue.severity ?? "error",
      ...words,
      remedy: issue.remedy === undefined ? null : { id: issue.remedy, label: REMEDY_LABELS[issue.remedy] },
      alternative: issue.alternative === undefined ? null : { id: issue.alternative, label: REMEDY_LABELS[issue.alternative] },
      ...place,
      kind,
      ignorable,
      ignoreKey: ignorable ? issueIgnoreKey(kind as string, place.target) : null,
      kindKey: ignorable ? issueIgnoreKey(kind as string, { kind: "map" }) : null,
      ignored,
      ...(issue.frame === undefined ? {} : { frame: issue.frame }),
    });
  }
  const ordered = [...entries.filter((e) => e.severity === "error"), ...entries.filter((e) => e.severity === "warning"), ...entries.filter((e) => e.severity === "info")];
  return { shown: ordered.filter((e) => e.ignored === null), ignored: ordered.filter((e) => e.ignored !== null), dormant: Object.keys(map.ignoredIssues ?? {}).filter((key) => !matched.has(key)).length };
}
